import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { backendConfig } from '../config/backendConfig';
import { FaHeart, FaRegHeart, FaComment } from 'react-icons/fa';
import CustomIcon from '../components/CustomIcon';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function MentorsCoLearning() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const q = useQuery();
  const ownerId = q.get('ownerId') || '';
  const code = q.get('code') || '';
  const [scope, setScope] = useState(q.get('scope') === 'global' ? 'global' : 'org');
  const [classFilter, setClassFilter] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [comments, setComments] = useState([]);
  const [likes, setLikes] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isMentor, setIsMentor] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false);
  const [artworkToUnshare, setArtworkToUnshare] = useState(null);
  const [isOrganizationUser, setIsOrganizationUser] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Update scope when query parameter changes
  useEffect(() => {
    const queryScope = q.get('scope');
    if (queryScope === 'global' || queryScope === 'org') {
      setScope(queryScope);
    }
  }, [q]);

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) return;
    
    // Access gate: require Firebase Auth user OR a valid code in query OR mentor_session
    // Also allow if mentor_session exists (for backward compatibility)
    try {
      const s = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      const hasSession = !!(s && s.ownerId && s.email);
      const hasAuth = !!(user && user.uid);
      
      console.log('[MentorsCoLearning] Auth check:', { hasAuth, hasSession, code: !!code });
      
      if (!hasAuth && !hasSession && !code) {
        console.log('[MentorsCoLearning] No auth, session, or code - redirecting to home');
        navigate('/');
        return;
      }
    } catch (e) {
      console.error('[MentorsCoLearning] Error checking session:', e);
      if (!user && !code) { 
        navigate('/'); 
        return; 
      }
    }
    const fetchItems = async () => {
      // Check for mentor_session if no Firebase user
      const s = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      const hasSession = !!(s && s.ownerId && s.email);
      
      if (!user && !code && !hasSession) {
        console.log('[MentorsCoLearning] No auth, code, or session - skipping fetch');
        return; // Need auth, code, or session to fetch
      }
      
      setLoading(true);
      try {
        let userParentId = null;
        
        // Get user's parentId for org filtering
        if (scope === 'org') {
          if (user) {
            try {
              const userDocRef = doc(db, 'users', user.uid);
              const userDoc = await getDoc(userDocRef);
              if (userDoc.exists()) {
                const userData = userDoc.data();
                // For organization users, use primaryId if available, otherwise use parentId or uid
                if (userData.mentorType === 'organization') {
                  userParentId = userData.primaryId || userData.parentId || userData.orgId || user.uid;
                  // If user is not primaryId but has primaryId, use that
                  if (!userData.isPrimaryId && userData.primaryId) {
                    userParentId = userData.primaryId;
                  }
                } else {
                  // For regular users, use parentId or uid
                  userParentId = userData.parentId || userData.primaryId || userData.orgId || user.uid;
                }
              } else {
                userParentId = user.uid;
              }
            } catch (error) {
              console.error('Error fetching user document:', error);
              userParentId = user.uid;
            }
          } else if (hasSession) {
            // For mentor_session, use ownerId from session
            userParentId = s.ownerId;
          }
        }
        
        // Use ownerId from query if provided, otherwise use user's parentId
        const targetParentId = ownerId || userParentId;
        
        // Use backend API for fetching shared artworks (works for both Firebase users and mentor_session)
        // This bypasses Firestore security rules
        const params = new URLSearchParams({ scope });
        if (targetParentId && scope === 'org') {
          params.append('ownerId', targetParentId);
        }
        if (hasSession && s.email) {
          params.append('email', s.email);
        }
        
        const apiUrl = `${backendConfig.baseUrl}/api/mentors/shared-artworks?${params.toString()}`;
        console.log('[MentorsCoLearning] Fetching shared artworks from:', apiUrl);
        
        const response = await fetch(apiUrl);
        console.log('[MentorsCoLearning] Response status:', response.status, response.statusText);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[MentorsCoLearning] Error response:', errorText);
          throw new Error(`Failed to fetch shared artworks: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('[MentorsCoLearning] Received result:', { ok: result.ok, artworkCount: result.artworks?.length || 0 });
        
        if (!result.ok) {
          throw new Error(result.error || 'Failed to fetch shared artworks');
        }
        
        setItems(result.artworks || []);
      } catch (error) {
        console.error('Error fetching shared artworks:', error);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    
    // Fetch items when scope, ownerId, or user changes
    // For global scope: all mentors (Firebase users or mentor_session) can access
    // For org scope: need ownerId (from query, user document, or mentor_session)
    let hasMentorSession = false;
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      hasMentorSession = !!(session && session.ownerId && session.email);
    } catch (_) {}
    
    if (scope === 'global' || ownerId || (user && scope === 'org') || (hasMentorSession && scope === 'org')) {
      fetchItems();
    }
  }, [scope, ownerId, user, code, authLoading, q]);

  // Check if user is a mentor
  useEffect(() => {
    const checkMentorStatus = async () => {
      // Check for mentor_session
      let hasMentorSession = false;
      try {
        const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
        hasMentorSession = !!(session && session.ownerId && session.email);
      } catch (_) {}
      
      if (!user && !code && !hasMentorSession) {
        setIsMentor(false);
        return;
      }
      
      if (user) {
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Check if user is a mentor (has accountType or mentorType)
            const isMentorUser = userData.accountType === 'mentor' || 
                                userData.mentorType === 'organization' || 
                                userData.mentorType === 'individual' ||
                                userData.mentorType === 'single' ||
                                userData.mentorType === 'mentor';
            console.log('checkMentorStatus: User data:', {
              accountType: userData.accountType,
              mentorType: userData.mentorType,
              isMentorUser
            });
            setIsMentor(isMentorUser);
            setIsOrganizationUser(userData.mentorType === 'organization');
          } else {
            console.log('checkMentorStatus: User document does not exist');
            setIsMentor(false);
          }
        } catch (error) {
          console.error('Error checking mentor status:', error);
          setIsMentor(false);
        }
      } else if (code || hasMentorSession) {
        // Access code user or mentor_session - they are mentors, can view and comment
        // For org mentors via access code, they should be able to comment
        setIsMentor(true);
        // Check if it's an organization mentor by checking mentor_session
        if (hasMentorSession) {
          setIsOrganizationUser(true);
        }
      } else {
        setIsMentor(false);
      }
    };
    
    checkMentorStatus();
  }, [user, code]);

  // Auto-open artwork modal if artworkId is in query
  useEffect(() => {
    const artworkId = q.get('artworkId');
    const queryScope = q.get('scope');
    
    // Update scope if provided in query
    if (queryScope && (queryScope === 'global' || queryScope === 'org')) {
      setScope(queryScope);
    }
    
    if (artworkId && items.length > 0 && !selectedArtwork) {
      // Try to find artwork by ID (sharedArtworks document ID)
      let artwork = items.find(item => item.id === artworkId);
      
      // If not found by ID, try to find by artworkUrl (in case artworkId is a URL)
      if (!artwork) {
        artwork = items.find(item => item.artworkUrl === artworkId || item.thumbnailUrl === artworkId);
      }
      
      if (artwork) {
        setSelectedArtwork(artwork);
        // Remove artworkId from URL but keep scope
        const newSearch = new URLSearchParams(q);
        newSearch.delete('artworkId');
        navigate(`?${newSearch.toString()}`, { replace: true });
      }
    }
  }, [items, q, selectedArtwork, navigate]);

  // Fetch comments and likes when artwork is selected
  useEffect(() => {
    if (!selectedArtwork) {
      setComments([]);
      setLikes([]);
      return;
    }

    setLoadingComments(true);
    
    // Use backend API to fetch comments (works for both Firebase users and mentor_session)
    const fetchComments = async () => {
      try {
        const response = await fetch(`${backendConfig.baseUrl}/api/comments/get?artworkId=${encodeURIComponent(selectedArtwork.id)}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch comments: ${response.statusText}`);
        }
        
        const result = await response.json();
        if (result.success && result.comments) {
          // Transform backend response to match frontend format
          const commentsList = result.comments.map(comment => ({
            id: comment.id,
            ...comment,
            createdAt: comment.createdAt || comment.timestamp,
            replies: comment.replies || []
          }));
          
          setComments(commentsList);
        } else {
          setComments([]);
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    };
    
    fetchComments();
    
    // Set up polling to refresh comments every 5 seconds (since we can't use real-time for mentor_session)
    const pollInterval = setInterval(fetchComments, 5000);

    // Fetch likes from the artwork document (try backend API first, fallback to Firestore)
    const fetchLikes = async () => {
      try {
        // Try to get likes from sharedArtworks via backend
        const response = await fetch(`${backendConfig.baseUrl}/api/mentors/shared-artworks?scope=global`);
        if (response.ok) {
          const result = await response.json();
          if (result.ok && result.artworks) {
            const artwork = result.artworks.find(a => a.id === selectedArtwork.id);
            if (artwork) {
              // Likes might be in the artwork data, but we need to fetch from the document
              // For now, try Firestore if user is authenticated
              if (user) {
                const artworkDocRef = doc(db, 'sharedArtworks', selectedArtwork.id);
                const artworkDoc = await getDoc(artworkDocRef);
                if (artworkDoc.exists()) {
                  const data = artworkDoc.data();
                  setLikes(data.likes || []);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching likes:', error);
        // Fallback: try Firestore if user is authenticated
        if (user) {
          try {
            const artworkDocRef = doc(db, 'sharedArtworks', selectedArtwork.id);
            const artworkDoc = await getDoc(artworkDocRef);
            if (artworkDoc.exists()) {
              const data = artworkDoc.data();
              setLikes(data.likes || []);
            }
          } catch (firestoreError) {
            console.error('Error fetching likes from Firestore:', firestoreError);
          }
        }
      }
    };
    
    fetchLikes();
    const likesPollInterval = setInterval(fetchLikes, 10000); // Poll likes every 10 seconds

    return () => {
      clearInterval(pollInterval);
      clearInterval(likesPollInterval);
    };
  }, [selectedArtwork, user]);

  const shown = items.filter(it => {
    // Filter by class - normalize both values for comparison
    if (classFilter) {
      const artworkClass = (it.classStandard || '').toString().trim();
      const filterClass = classFilter.toString().trim();
      
      // Extract number from "Class 7" or just "7" - handle both formats
      const extractClassNum = (str) => {
        const num = str.replace(/class\s*/i, '').trim();
        return num;
      };
      
      const artworkClassNum = extractClassNum(artworkClass);
      const filterClassNum = extractClassNum(filterClass);
      
      // Compare: match if numbers are equal (e.g., "7" matches "Class 7")
      if (artworkClassNum !== filterClassNum) {
        return false;
      }
    }
    
    // Filter by assignment type - use partial matching for techniques
    if (assignmentFilter) {
      const artworkType = (it.assignmentType || '').toString().toLowerCase();
      const filterType = assignmentFilter.toString().toLowerCase();
      // Check exact match or if artwork type contains the filter type
      if (artworkType !== filterType && !artworkType.includes(filterType) && !filterType.includes(artworkType)) {
        return false;
      }
    }
    
    return true;
  });

  // Handle adding a comment or reply
  const handleAddComment = async (parentCommentId = null) => {
    const commentText = parentCommentId ? replyText : newComment;
    
    console.log('handleAddComment called:', {
      commentText: commentText?.trim(),
      hasSelectedArtwork: !!selectedArtwork,
      isMentor,
      hasUser: !!user,
      parentCommentId
    });
    
    if (!commentText || !commentText.trim()) {
      console.error('Comment text is empty');
      alert('Please enter a comment');
      return;
    }
    
    if (!selectedArtwork) {
      console.error('No artwork selected');
      alert('No artwork selected');
      return;
    }
    
    // Check for mentor_session if no Firebase user
    const s = JSON.parse(localStorage.getItem('mentor_session') || '{}');
    const hasSession = !!(s && s.ownerId && s.email);
    
    if (!user && !hasSession) {
      console.error('User not authenticated');
      alert('Please log in to post comments');
      return;
    }
    
    if (!isMentor && !hasSession) {
      console.error('User is not a mentor:', { isMentor, user, hasSession });
      alert('Only mentors can post comments');
      return;
    }

    try {
      // Get user info from mentor_session if no Firebase user
      const s = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      const userId = user?.uid || s.ownerId || s.email;
      const userName = user?.displayName || user?.email || s.email || user?.mentorName || 'Anonymous Mentor';
      
      const commentData = {
        artworkId: selectedArtwork.id,
        userId: userId,
        userName: userName,
        commentText: commentText.trim()
      };

      // Add parentCommentId if this is a reply
      if (parentCommentId) {
        commentData.parentCommentId = parentCommentId;
      }

      console.log('Adding comment via backend API:', commentData);
      
      // Use backend API to add comment (works for both Firebase users and mentor_session)
      const response = await fetch(`${backendConfig.baseUrl}/api/comments/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(commentData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add comment');
      }
      
      const result = await response.json();
      console.log('Comment added successfully:', result);
      
      // Clear comment input
      if (parentCommentId) {
        setReplyText('');
        setReplyingTo(null);
      } else {
        setNewComment('');
      }
      
      // Refresh comments after a short delay
      setTimeout(() => {
        // Comments will be refreshed by the polling interval
      }, 1000);
      
      // Notifications are handled by the backend API
    } catch (error) {
      console.error('Error adding comment:', error);
      alert(error.message || 'Failed to add comment. Please try again.');
    }
  };

  // Handle toggling like
  const handleToggleLike = async () => {
    if (!selectedArtwork || !isMentor || !user) {
      return;
    }

    try {
      const artworkDocRef = doc(db, 'sharedArtworks', selectedArtwork.id);
      const isLiked = likes.includes(user.uid);

      if (isLiked) {
        await updateDoc(artworkDocRef, {
          likes: arrayRemove(user.uid)
        });
      } else {
        await updateDoc(artworkDocRef, {
          likes: arrayUnion(user.uid)
        });

        // Create notification for the artwork owner
        try {
          const artworkDoc = await getDoc(artworkDocRef);
          if (artworkDoc.exists()) {
            const artworkData = artworkDoc.data();
            const ownerId = artworkData.mentorId;
            
            console.log('Like notification check:', {
              ownerId,
              currentUserId: user.uid,
              artworkId: selectedArtwork.id
            });
            
            if (ownerId && ownerId !== user.uid) {
              const notificationData = {
                userId: ownerId,
                recipientId: ownerId, // Add recipientId for compatibility
                type: 'like',
                title: 'Your Artwork Was Liked',
                message: `${user.displayName || user.email || user.mentorName || 'A mentor'} liked "${selectedArtwork.assignmentTitle || selectedArtwork.studentName || 'your artwork'}"`,
                artworkId: selectedArtwork.id,
                artworkTitle: selectedArtwork.assignmentTitle || selectedArtwork.studentName,
                artworkUrl: selectedArtwork.thumbnailUrl,
                fromUserId: user.uid,
                fromUserName: user.displayName || user.email || user.mentorName || 'A mentor',
                read: false,
                createdAt: serverTimestamp()
              };
              
              console.log('Creating like notification:', notificationData);
              const notificationRef = await addDoc(collection(db, 'notifications'), notificationData);
              console.log('Like notification created successfully:', notificationRef.id);
            } else {
              console.log('No like notification created - ownerId:', ownerId, 'currentUserId:', user.uid);
            }
          } else {
            console.error('Artwork document does not exist for like:', selectedArtwork.id);
          }
        } catch (notifError) {
          console.error('Error creating like notification:', notifError);
          console.error('Like notification error details:', {
            message: notifError.message,
            code: notifError.code,
            stack: notifError.stack
          });
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      alert('Failed to update like. Please try again.');
    }
  };

  // Handle unshare artwork
  const handleUnshare = async () => {
    if (!artworkToUnshare || !user) {
      return;
    }

    // Verify that the current user is the owner before unsharing
    if (artworkToUnshare.mentorId !== user.uid) {
      alert('You can only unshare artworks that you uploaded.');
      setShowUnshareConfirm(false);
      setArtworkToUnshare(null);
      return;
    }

    try {
      const artworkDocRef = doc(db, 'sharedArtworks', artworkToUnshare.id);
      
      // Update status to inactive to remove from learning space
      await updateDoc(artworkDocRef, {
        status: 'inactive',
        unsharedAt: serverTimestamp()
      });

      // Immediately remove the artwork from the list for instant feedback
      setItems(prevItems => prevItems.filter(item => item.id !== artworkToUnshare.id));
      
      // Close the artwork detail modal if it's open for this artwork
      if (selectedArtwork && selectedArtwork.id === artworkToUnshare.id) {
        setSelectedArtwork(null);
      }

      // Close confirmation modal
      setShowUnshareConfirm(false);
      setArtworkToUnshare(null);
      
      // Show success message
      alert('Artwork unshared successfully! 🎨');
    } catch (error) {
      console.error('Error unsharing artwork:', error);
      alert('Failed to unshare artwork. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8E1]">
      <div className="mx-auto max-w-7xl px-4 py-4">
        {/* Top navigation for mentors */}
        <div className="flex items-center justify-end gap-2 mb-3">
          <button className="px-3 py-1 rounded-full border bg-white border-amber-300 text-amber-800" onClick={()=>navigate('/')}>Artist Corner</button>
          <button 
            className="px-3 py-1 rounded-full border bg-white border-amber-300 text-amber-800" 
            onClick={()=>navigate(isOrganizationUser ? '/organization-dashboard' : '/mentor')}
          >
            {isOrganizationUser ? 'Organization Dashboard' : 'Mentors Corner'}
          </button>
          <button className="px-3 py-1 rounded-full border bg-white border-amber-300 text-amber-800" onClick={()=>navigate('/create-books')}>Artmosphere Collections</button>
        </div>
        {/* Title */}
        <h1 className="text-center text-2xl md:text-3xl font-extrabold text-amber-900" style={{ fontFamily: 'Fredoka, sans-serif' }}>Artmosphere Mentor's Co-learning Space</h1>
        {/* Scope toggle */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <button 
            className={`px-3 py-1 rounded-full border transition-colors ${scope==='org'?'bg-amber-100 border-amber-300 text-amber-900':'bg-white border-slate-200 text-slate-700 hover:bg-amber-50'}`} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setScope('org');
              // Update URL using React Router navigate
              const newSearch = new URLSearchParams(q);
              newSearch.set('scope', 'org');
              if (ownerId) {
                newSearch.set('ownerId', ownerId);
              }
              navigate(`?${newSearch.toString()}`, { replace: true });
            }}
          >
            Org Learning Space
          </button>
          <button 
            className={`px-3 py-1 rounded-full border transition-colors ${scope==='global'?'bg-amber-100 border-amber-300 text-amber-900':'bg-white border-slate-200 text-slate-700 hover:bg-amber-50'}`} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setScope('global');
              // Update URL using React Router navigate
              const newSearch = new URLSearchParams(q);
              newSearch.set('scope', 'global');
              if (ownerId) {
                newSearch.set('ownerId', ownerId);
              }
              navigate(`?${newSearch.toString()}`, { replace: true });
            }}
          >
            Artmosphere Co-learning
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select className="border rounded-lg px-3 py-2" value={classFilter} onChange={e=>setClassFilter(e.target.value)}>
            <option value="">All Classes</option>
            <option value="1">Class 1</option>
            <option value="2">Class 2</option>
            <option value="3">Class 3</option>
            <option value="4">Class 4</option>
            <option value="5">Class 5</option>
            <option value="6">Class 6</option>
            <option value="7">Class 7</option>
            <option value="8">Class 8</option>
            <option value="9">Class 9</option>
            <option value="10">Class 10</option>
            <option value="11">Class 11</option>
            <option value="12">Class 12</option>
          </select>
          <select className="border rounded-lg px-3 py-2" value={assignmentFilter} onChange={e=>setAssignmentFilter(e.target.value)}>
            <option value="">All Assignment Types</option>
            <option value="color_mood">Color & Mood</option>
            <option value="lines_shapes">Lines & Shapes</option>
            <option value="composition">Composition</option>
            <option value="storyboarding">Storyboarding</option>
          </select>
        </div>

        <div className="mt-6">
          {loading || authLoading ? (
            <div className="text-amber-700">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {shown.length === 0 ? (
                <div className="col-span-full text-center py-12 text-amber-700">
                  <p className="text-lg">No shared artworks found.</p>
                  <p className="text-sm mt-2">Share artworks from your Mentor Activity Dashboard to see them here.</p>
                </div>
              ) : (
                shown.map(it => {
                  // Check if current user is the owner of this artwork
                  const isOwner = user && it.mentorId === user.uid;
                  
                  return (
                    <div key={it.id} className="rounded-2xl border border-amber-200 bg-white shadow hover:shadow-md overflow-hidden">
                      <div className="flex items-center justify-between px-4 pt-4">
                        <div className="text-sm font-semibold text-amber-900">{it.assignmentTitle || it.studentName || 'Shared Artwork'}</div>
                      </div>
                      {it.studentName && (
                        <div className="px-4 pt-1">
                          <div className="text-xs text-slate-600">by {it.studentName}</div>
                        </div>
                      )}
                      <div className="px-4 pt-2 pb-3">
                        <div className="rounded-xl overflow-hidden border border-amber-100 aspect-[3/2] bg-amber-50">
                          {it.thumbnailUrl && (<img src={it.thumbnailUrl} alt={it.assignmentTitle||it.studentName||'artwork'} className="w-full h-full object-cover" />)}
                        </div>
                        <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                          <div>Class: {it.classStandard || '-'}</div>
                          {it.assignmentType && <div>Techniques: {it.assignmentType}</div>}
                        </div>
                        {it.mentorComments && (
                          <div className="mt-2 text-xs text-slate-600 italic">
                            "{it.mentorComments.substring(0, 60)}{it.mentorComments.length > 60 ? '...' : ''}"
                          </div>
                        )}
                        {it.mentorName && (
                          <div className="mt-1 text-xs text-slate-500">
                            Shared by {it.mentorName}
                          </div>
                        )}
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <button className="rounded-full bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-sm flex-1" onClick={() => setSelectedArtwork(it)}>View Artwork</button>
                          {isOwner && (
                            <button 
                              className="rounded-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 text-sm" 
                              onClick={() => {
                                setArtworkToUnshare(it);
                                setShowUnshareConfirm(true);
                              }}
                              title="Unshare this artwork"
                            >
                              Unshare
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Artwork Detail Modal */}
      {selectedArtwork && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setSelectedArtwork(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-yellow-500 text-white p-6 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">
                  {selectedArtwork.assignmentTitle || selectedArtwork.studentName || 'Artwork Details'}
                </h2>
                {selectedArtwork.studentName && (
                  <p className="text-amber-100 mt-1">by {selectedArtwork.studentName}</p>
                )}
              </div>
              <button
                onClick={() => setSelectedArtwork(null)}
                className="text-white hover:text-amber-100 text-3xl leading-none transition-colors duration-200"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Artwork Image and Like/Comment Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-center bg-gray-50 rounded-xl p-4">
                    {selectedArtwork.thumbnailUrl && (
                      <img
                        src={selectedArtwork.thumbnailUrl}
                        alt={selectedArtwork.assignmentTitle || selectedArtwork.studentName || 'Artwork'}
                        className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                      />
                    )}
                  </div>

                  {/* Like and Comment Section */}
                  <div className="bg-white border border-amber-200 rounded-xl p-4">
                    {/* Like Button */}
                    <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
                      <button
                        onClick={handleToggleLike}
                        disabled={!isMentor}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                          isMentor 
                            ? likes.includes(user?.uid) 
                              ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {likes.includes(user?.uid) ? (
                          <CustomIcon name="heart" filled={true} className="w-5 h-5" alt="Liked" />
                        ) : (
                          <CustomIcon name="heart" filled={false} className="w-5 h-5" alt="Like" />
                        )}
                        <span className="font-semibold">{likes.length}</span>
                        <span>{likes.length === 1 ? 'Like' : 'Likes'}</span>
                      </button>
                      {!isMentor && (
                        <span className="text-xs text-gray-500">Sign in as mentor to like</span>
                      )}
                    </div>

                    {/* Comments Section */}
                    <div>
                      <h3 className="text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                        <CustomIcon name="comment" className="w-5 h-5 text-amber-600" alt="Comments" />
                        Comments ({comments.length})
                      </h3>

                      {/* Comment Input (only for mentors) */}
                      {isMentor && (
                        <div className="mb-4">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="w-full px-4 py-2 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                            rows="3"
                          />
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAddComment();
                            }}
                            disabled={!newComment.trim()}
                            className="mt-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-colors duration-200"
                          >
                            Post Comment
                          </button>
                        </div>
                      )}

                      {/* Comments List */}
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {loadingComments ? (
                          <p className="text-gray-500 text-sm">Loading comments...</p>
                        ) : comments.length === 0 ? (
                          <p className="text-gray-500 text-sm italic">No comments yet. Be the first to comment!</p>
                        ) : (
                          comments.map((comment) => (
                            <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-start justify-between mb-1">
                                <p className="font-semibold text-sm text-gray-900">{comment.mentorName || 'Anonymous'}</p>
                                {comment.createdAt && (
                                  <p className="text-xs text-gray-500">
                                    {comment.createdAt.toDate ? 
                                      new Date(comment.createdAt.toDate()).toLocaleDateString() : 
                                      'Recently'}
                                  </p>
                                )}
                              </div>
                              <p className="text-sm text-gray-700 mb-2">{comment.comment}</p>
                              
                              {/* Reply button */}
                              {isMentor && (
                                <button
                                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                                  className="text-xs text-amber-600 hover:text-amber-800 font-medium"
                                >
                                  {replyingTo === comment.id ? 'Cancel' : 'Reply'}
                                </button>
                              )}
                              
                              {/* Reply form */}
                              {replyingTo === comment.id && isMentor && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Write a reply..."
                                    className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none text-sm"
                                    rows="2"
                                  />
                                  <button
                                    onClick={() => handleAddComment(comment.id)}
                                    disabled={!replyText.trim()}
                                    className="mt-2 px-3 py-1 bg-amber-500 hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors duration-200"
                                  >
                                    Post Reply
                                  </button>
                                </div>
                              )}
                              
                              {/* Display replies */}
                              {comment.replies && comment.replies.length > 0 && (
                                <div className="mt-3 ml-4 space-y-2 border-l-2 border-amber-200 pl-3">
                                  {comment.replies.map((reply) => (
                                    <div key={reply.id} className="bg-amber-50 rounded-lg p-2">
                                      <div className="flex items-start justify-between mb-1">
                                        <p className="font-semibold text-xs text-gray-900">{reply.mentorName || 'Anonymous'}</p>
                                        {reply.createdAt && (
                                          <p className="text-xs text-gray-500">
                                            {reply.createdAt.toDate ? 
                                              new Date(reply.createdAt.toDate()).toLocaleDateString() : 
                                              'Recently'}
                                          </p>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-700">{reply.comment}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Artwork Details */}
                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-xl p-4">
                    <h3 className="text-lg font-bold text-amber-900 mb-3">Artwork Information</h3>
                    <div className="space-y-3">
                      {selectedArtwork.classStandard && (
                        <div>
                          <span className="text-sm font-semibold text-amber-800">Class:</span>
                          <span className="ml-2 text-amber-900">{selectedArtwork.classStandard}</span>
                        </div>
                      )}
                      {selectedArtwork.section && (
                        <div>
                          <span className="text-sm font-semibold text-amber-800">Section:</span>
                          <span className="ml-2 text-amber-900">{selectedArtwork.section}</span>
                        </div>
                      )}
                      {selectedArtwork.schoolName && (
                        <div>
                          <span className="text-sm font-semibold text-amber-800">School:</span>
                          <span className="ml-2 text-amber-900">{selectedArtwork.schoolName}</span>
                        </div>
                      )}
                      {selectedArtwork.assignmentType && (
                        <div>
                          <span className="text-sm font-semibold text-amber-800">Techniques:</span>
                          <span className="ml-2 text-amber-900">{selectedArtwork.assignmentType}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedArtwork.mentorComments && (
                    <div className="bg-yellow-50 rounded-xl p-4">
                      <h3 className="text-lg font-bold text-amber-900 mb-2">Mentor Comments</h3>
                      <p className="text-amber-800 italic leading-relaxed">{selectedArtwork.mentorComments}</p>
                    </div>
                  )}

                  {selectedArtwork.mentorName && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Shared by:</span> {selectedArtwork.mentorName}
                      </p>
                    </div>
                  )}

                  {/* Close Button */}
                  <div className="pt-4">
                    <button
                      onClick={() => setSelectedArtwork(null)}
                      className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-semibold transition-colors duration-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unshare Confirmation Modal */}
      {showUnshareConfirm && artworkToUnshare && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full border-4 border-amber-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 px-6 py-4">
              <h3 className="text-2xl font-bold text-white">Unshare Artwork</h3>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-800 text-lg mb-2">
                  Are you sure you want to unshare this artwork?
                </p>
                <p className="text-gray-600 text-sm">
                  This artwork will be removed from the learning space and will no longer be visible to other mentors.
                </p>
              </div>
              
              {artworkToUnshare.thumbnailUrl && (
                <div className="mb-4 rounded-xl overflow-hidden border-2 border-amber-200">
                  <img 
                    src={artworkToUnshare.thumbnailUrl} 
                    alt={artworkToUnshare.assignmentTitle || 'Artwork'} 
                    className="w-full h-32 object-cover"
                  />
                </div>
              )}
              
              <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-semibold text-amber-900 mb-1">
                  {artworkToUnshare.assignmentTitle || 'Untitled Artwork'}
                </p>
                {artworkToUnshare.studentName && (
                  <p className="text-xs text-amber-700">by {artworkToUnshare.studentName}</p>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => {
                  setShowUnshareConfirm(false);
                  setArtworkToUnshare(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUnshare}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Unshare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


